import { afterNextRender, DOCUMENT, inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';

const SPLASH_ELEMENT_ID = 'app-splash';
const SPLASH_HIDDEN_CLASS = 'app-splash--hidden';

/**
 * Takes down the loader that `index.html` paints before Angular boots.
 *
 * The loader lives outside `<app-root>`, so nothing removes it implicitly — see the comment
 * on `#app-splash` in `index.html` for why it has to outlive bootstrap, and for the CSS
 * safety timeout that covers a startup which never reaches either event below.
 */
@Injectable({ providedIn: 'root' })
export class SplashScreenService {
  #router = inject(Router);
  #injector = inject(Injector);
  #document = inject(DOCUMENT);

  constructor() {
    this.#router.events
      .pipe(
        // A guard that redirects cancels its navigation without ending it, so the first
        // NavigationEnd is the first page the user actually gets — login and the account-state
        // screens included. NavigationError means startup gave up and nothing will render.
        filter((routerEvent) => routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationError),
        take(1),
      )
      .subscribe(() => {
        // NavigationEnd fires once the route is activated but before the activated component's
        // template is written to the DOM, so hiding here would still flash an empty screen.
        afterNextRender(() => this.#hide(), { injector: this.#injector });
      });
  }

  #hide(): void {
    this.#document.getElementById(SPLASH_ELEMENT_ID)?.classList.add(SPLASH_HIDDEN_CLASS);
  }
}
